import { useEffect, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import { Upload, X, FileAudio, Pencil, Play, Wand2, GripVertical, RotateCw, FlipHorizontal, FlipVertical } from "lucide-react";
import { toast } from "sonner";
import { api, abs, errMsg } from "../../lib/api";
import { Dialog, DialogContent, DialogTitle } from "../../components/ui/dialog";

export function useUpload() {
  const [progress, setProgress] = useState(null);
  const upload = async (file, kind) => {
    const fd = new FormData();
    fd.append("file", file);
    setProgress(0);
    try {
      const { data } = await api.post("/admin/uploads", fd, {
        params: { kind },
        onUploadProgress: (e) => setProgress(Math.round((e.loaded / (e.total || 1)) * 100)),
      });
      return data;
    } catch (e) {
      toast.error(`${file.name}: ${errMsg(e)}`);
      return null;
    } finally {
      setProgress(null);
    }
  };
  return { upload, progress };
}

const DropButton = ({ accept, multiple, onFiles, progress, label, testid }) => {
  const ref = useRef(null);
  return (
    <>
      <input ref={ref} type="file" accept={accept} multiple={multiple} className="hidden" data-testid={`${testid}-input`}
        onChange={(e) => { onFiles([...e.target.files]); e.target.value = ""; }} />
      <button type="button" onClick={() => ref.current.click()} disabled={progress !== null} className="btn-ghost !py-2.5" data-testid={`${testid}-button`}>
        <Upload size={14} /> {progress !== null ? `Uploading ${progress}%` : label}
      </button>
    </>
  );
};

const loadImage = (src) => new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = src; });

async function renderEdit(src, area, rotation, flip, type) {
  const img = await loadImage(src);
  const rad = (rotation * Math.PI) / 180;
  const bw = Math.round(Math.abs(Math.cos(rad) * img.width) + Math.abs(Math.sin(rad) * img.height));
  const bh = Math.round(Math.abs(Math.sin(rad) * img.width) + Math.abs(Math.cos(rad) * img.height));
  const c = document.createElement("canvas");
  c.width = bw; c.height = bh;
  const ctx = c.getContext("2d");
  ctx.translate(bw / 2, bh / 2); ctx.rotate(rad); ctx.scale(flip.h ? -1 : 1, flip.v ? -1 : 1);
  ctx.translate(-img.width / 2, -img.height / 2); ctx.drawImage(img, 0, 0);
  const out = document.createElement("canvas");
  out.width = Math.round(area.width); out.height = Math.round(area.height);
  out.getContext("2d").drawImage(c, area.x, area.y, area.width, area.height, 0, 0, out.width, out.height);
  return new Promise((res) => out.toBlob(res, type, 0.92));
}

const ASPECTS = [["Original", null], ["1:1", 1], ["4:5", 4 / 5], ["3:2", 3 / 2], ["16:9", 16 / 9]];

export function ImageEditor({ mediaId, onClose, onSaved }) {
  const [src, setSrc] = useState(null);
  const [type, setType] = useState("image/jpeg");
  const [natural, setNatural] = useState(1);
  const [aspect, setAspect] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [flip, setFlip] = useState({ h: false, v: false });
  const [area, setArea] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let url;
    api.get(`/admin/media/${mediaId}/raw`, { responseType: "blob" })
      .then((r) => { url = URL.createObjectURL(r.data); setType(r.data.type === "image/png" ? "image/png" : "image/jpeg"); setSrc(url); })
      .catch((e) => toast.error(errMsg(e)));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [mediaId]);
  const rotated = rotation % 180 !== 0;
  const save = async () => {
    setBusy(true);
    try {
      const blob = await renderEdit(src, area, rotation, flip, type);
      const fd = new FormData();
      fd.append("file", new File([blob], type === "image/png" ? "edited.png" : "edited.jpg", { type }));
      const { data } = await api.post(`/admin/media/${mediaId}/version`, fd);
      toast.success("Edited version saved"); onSaved(data); onClose();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const tool = "btn-ghost !px-3 !py-2";
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl rounded-none" data-testid="image-editor">
        <DialogTitle className="font-display text-3xl font-normal">Edit image</DialogTitle>
        <div className="relative h-[420px] bg-black">
          {src && (
            <Cropper image={src} crop={crop} zoom={zoom} rotation={rotation} aspect={aspect || (rotated ? 1 / natural : natural)}
              onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={(_, px) => setArea(px)}
              onMediaLoaded={(m) => setNatural(m.naturalWidth / m.naturalHeight)}
              transform={`translate(${crop.x}px, ${crop.y}px) rotate(${rotation}deg) scale(${zoom}) scaleX(${flip.h ? -1 : 1}) scaleY(${flip.v ? -1 : 1})`} />
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" className={tool} onClick={() => setRotation((r) => (r + 90) % 360)} data-testid="editor-rotate"><RotateCw size={14} /> Rotate 90°</button>
          <button type="button" className={tool} onClick={() => setFlip((f) => ({ ...f, h: !f.h }))} data-testid="editor-flip-h"><FlipHorizontal size={14} /> Flip H</button>
          <button type="button" className={tool} onClick={() => setFlip((f) => ({ ...f, v: !f.v }))} data-testid="editor-flip-v"><FlipVertical size={14} /> Flip V</button>
          <select className="wb-input !w-auto !py-2 text-xs" value={String(aspect)} onChange={(e) => setAspect(e.target.value === "null" ? null : Number(e.target.value))} data-testid="editor-aspect">
            {ASPECTS.map(([l, v]) => <option key={l} value={String(v)}>Crop {l}</option>)}
          </select>
          <input type="range" min={1} max={3} step={0.05} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} className="accent-[#D4AF37]" data-testid="editor-zoom" />
          <button type="button" onClick={save} disabled={busy || !area} className="btn-gold ml-auto" data-testid="editor-save">{busy ? "Saving…" : "Save as new version"}</button>
        </div>
        <p className="text-xs muted">Saved as a new file ({type === "image/png" ? "PNG, transparency kept" : "JPEG"}); the original is kept. Remember to save the product.</p>
      </DialogContent>
    </Dialog>
  );
}

export function ImagesField({ images, setImages, onMeta, kind = "image", testid = "upload-images" }) {
  const { upload, progress } = useUpload();
  const [editing, setEditing] = useState(null);
  const [drag, setDrag] = useState(null);
  const onFiles = async (files) => {
    for (const f of files) {
      const r = await upload(f, kind);
      if (r) { onMeta(r); setImages((prev) => [...prev, r.id]); }
    }
  };
  const makeCover = (id) => setImages((prev) => [id, ...prev.filter((x) => x !== id)]);
  const drop = (target) => {
    if (drag === null || drag === target) return;
    setImages((prev) => { const n = prev.filter((x) => x !== drag); n.splice(n.indexOf(target), 0, drag); return n; });
    setDrag(null);
  };
  return (
    <div data-testid="images-field">
      <div className="flex flex-wrap gap-4">
        {images.map((id, i) => (
          <div key={id} draggable onDragStart={() => setDrag(id)} onDragOver={(e) => e.preventDefault()} onDrop={() => drop(id)}
            className={`group relative h-28 w-28 cursor-move border wb-line ${drag === id ? "opacity-40" : ""}`} data-testid="product-image-thumb">
            <img src={abs(`/api/media/${id}`)} alt="" className="pointer-events-none h-full w-full object-cover" />
            <GripVertical size={14} className="absolute left-1 top-1 text-white drop-shadow" />
            {i === 0 ? <span className="absolute bottom-0 left-0 bg-wb-gold px-1.5 font-mono text-[0.55rem] uppercase text-[color:var(--wb-on-gold)]">Cover</span> : (
              <button type="button" onClick={() => makeCover(id)} className="absolute bottom-0 left-0 bg-black/70 px-1.5 font-mono text-[0.55rem] uppercase text-white opacity-0 group-hover:opacity-100" data-testid="product-image-make-cover">Make cover</button>
            )}
            <button type="button" onClick={() => setEditing(id)} className="absolute bottom-0 right-0 bg-black/70 p-1 text-white" aria-label="Edit image" data-testid="product-image-edit"><Pencil size={12} /></button>
            <button type="button" onClick={() => setImages((prev) => prev.filter((x) => x !== id))} className="absolute right-1 top-1 bg-black/70 p-1 text-white" aria-label="Remove image" data-testid="product-image-remove"><X size={12} /></button>
          </div>
        ))}
      </div>
      <p className="mt-3 text-xs muted">Drag thumbnails to reorder. The first image is the cover.</p>
      <div className="mt-4"><DropButton accept="image/*" multiple onFiles={onFiles} progress={progress} label="Upload images" testid={testid} /></div>
      {editing && <ImageEditor mediaId={editing} onClose={() => setEditing(null)} onSaved={(m) => { onMeta(m); setImages((prev) => prev.map((x) => (x === editing ? m.id : x))); }} />}
    </div>
  );
}

export function SingleImage({ label, kind, value, onChange, onMeta, fallback, testid, hint }) {
  const { upload, progress } = useUpload();
  const [editing, setEditing] = useState(false);
  const onFiles = async ([f]) => {
    const r = f && (await upload(f, kind));
    if (r) { onMeta?.(r); onChange(r.id); }
  };
  return (
    <div className="wb-card p-5" data-testid={testid}>
      <div className="wb-label">{label}</div>
      {hint && <p className="mb-3 text-xs muted">{hint}</p>}
      <div className="art-frame mb-4 aspect-[4/3] max-w-sm">
        <img src={value ? abs(`/api/media/${value}`) : fallback} alt="" className="h-full w-full object-cover" data-testid={`${testid}-preview`} />
        {!value && <span className="absolute left-2 top-2 bg-black/60 px-2 py-0.5 font-mono text-[0.55rem] uppercase text-white">Default</span>}
      </div>
      <div className="flex flex-wrap gap-2">
        <DropButton accept="image/*" onFiles={onFiles} progress={progress} label={value ? "Replace" : "Upload"} testid={`${testid}-upload`} />
        {value && <button type="button" className="btn-ghost !py-2.5" onClick={() => setEditing(true)} data-testid={`${testid}-edit`}><Pencil size={14} /> Edit</button>}
        {value && <button type="button" className="btn-ghost !py-2.5" onClick={() => onChange(null)} data-testid={`${testid}-reset`}>Use default</button>}
      </div>
      {editing && <ImageEditor mediaId={value} onClose={() => setEditing(false)} onSaved={(m) => { onMeta?.(m); onChange(m.id); }} />}
    </div>
  );
}

function AdminPlayer({ id, type }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  const load = async () => {
    try { const { data } = await api.post(`/admin/media/${id}/play-url`); setUrl(abs(data.url)); setFailed(false); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (!url) return <button type="button" onClick={load} className="btn-ghost !py-2.5" data-testid="admin-media-play"><Play size={14} /> Play</button>;
  const Tag = type?.startsWith("video/") ? "video" : "audio";
  return (
    <div className="mt-3 w-full">
      <Tag controls autoPlay src={url} onError={() => setFailed(true)} className="w-full" data-testid="admin-media-player" />
      {failed && <p className="mt-1 text-xs text-red-400" data-testid="admin-media-play-error">This file couldn't play in your browser.</p>}
    </div>
  );
}

function GeneratePreview({ fullId, onGenerated }) {
  const [start, setStart] = useState(0);
  const [len, setLen] = useState(30);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/media/${fullId}/generate-preview`, { start: Number(start), duration: Number(len) });
      onGenerated(data); toast.success("Preview clip generated from the full file");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="mt-4 border-t wb-line pt-4" data-testid="generate-preview">
      <div className="wb-label">Auto-generate preview clip</div>
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-xs muted">Start (s)<input type="number" min="0" step="1" value={start} onChange={(e) => setStart(e.target.value)} className="wb-input !w-24 !py-2" data-testid="generate-preview-start" /></label>
        <label className="text-xs muted">Length (s)<input type="number" min="3" max="120" step="1" value={len} onChange={(e) => setLen(e.target.value)} className="wb-input !w-24 !py-2" data-testid="generate-preview-length" /></label>
        <button type="button" onClick={run} disabled={busy} className="btn-ghost !py-2.5" data-testid="generate-preview-button"><Wand2 size={14} /> {busy ? "Generating…" : "Generate"}</button>
      </div>
    </div>
  );
}

export function FileSlot({ label, kind, accept, value, meta, onChange, onMeta, hint, onGenerated }) {
  const { upload, progress } = useUpload();
  const onFiles = async ([f]) => {
    const r = f && (await upload(f, kind));
    if (r) { onMeta(r); onChange(r.id); }
  };
  const m = value && meta[value];
  const playable = m?.content_type?.match(/^(audio|video)\//);
  return (
    <div className="wb-card p-5" data-testid={`file-slot-${kind}`}>
      <div className="wb-label">{label}</div>
      {hint && <p className="mb-3 text-xs muted">{hint}</p>}
      {value ? (
        <div className="mb-4 flex items-center gap-3 text-sm">
          <FileAudio size={16} className="gold" />
          <span className="truncate" data-testid={`file-slot-${kind}-name`}>{m?.filename || "Uploaded file"}</span>
          {m?.size ? <span className="font-mono text-xs muted">{(m.size / 1048576).toFixed(1)} MB</span> : null}
          <button type="button" onClick={() => onChange(null)} className="ml-auto muted hover:text-red-400" aria-label="Remove file" data-testid={`file-slot-${kind}-remove`}><X size={14} /></button>
        </div>
      ) : <p className="mb-4 text-xs muted">No file uploaded.</p>}
      <div className="flex flex-wrap gap-2">
        <DropButton accept={accept} onFiles={onFiles} progress={progress} label={value ? "Replace file" : "Upload file"} testid={`upload-${kind}`} />
        {value && playable && <AdminPlayer key={value} id={value} type={m.content_type} />}
      </div>
      {value && onGenerated && playable && <GeneratePreview fullId={value} onGenerated={onGenerated} />}
    </div>
  );
}
